import {
  useExportMaintenanceRpt,
  useLazyQueryParkingVideoRecordImage,
  useParkingVideoRecordRpt,
} from '@services/report'
import Flex from '@shared/Flex'
import { Button, Input } from '@shared/Form'
import { RangePicker } from '@shared/Form/DatePicker'
import { IconPhoto } from '@shared/icons'
import useSearch from '@shared/innmaxLib/hooks/useSearch'
import useVisible from '@shared/innmaxLib/hooks/useVisible'
import { rules } from '@shared/innmaxLib/utils/form'
import Layout from '@shared/Layout'
import Table from '@shared/Table'
import { downloadFile } from '@shared/utils/webHelper'
import { Form } from 'antd'
import { ColumnsType } from 'antd/es/table'
import type { Moment } from 'moment'
import moment from 'moment'
import { isNil } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormItem } from 'src/components/Form'
import OptionsSelect from 'src/components/OptionsSelect'
import ImageViewerModal from 'src/components/UploadButton/ImageViewerModal'

type RangeValue = [Moment | null, Moment | null] | null

function ParkingVideoRecord() {
  const { t } = useTranslation()

  const [form] = Form.useForm()

  const photoModal = useVisible<any>()

  const [photoModalData, setPhotoModalData] = React.useState<any>({})

  const { query, handleSearch, handleTableChange } = useSearch({
    form,
    keepPreviousQuery: false,
    transform: values => {
      const { reportDate, ...others } = values
      const [startAt, endAt] = reportDate ? reportDate : []

      const params = {
        ...others,
        start: startAt && moment(startAt).startOf('day').valueOf(),
        end: endAt && moment(endAt).endOf('day').valueOf(),
      }

      return params
    },
  })

  const { loading, dataSource } = useParkingVideoRecordRpt(query)

  const { data: imageData, refetch } = useLazyQueryParkingVideoRecordImage()

  const [exporting, handleExport] = useExportMaintenanceRpt(query, {
    onSuccess: (blob: any) => {
      downloadFile({
        blob,
        filename: 'report',
      })
    },
  })

  const columns: ColumnsType<any> = [
    {
      title: '開始停車時間',
      dataIndex: 'inTimeStr',
      align: 'center',
    },
    {
      title: '結束停車時間',
      dataIndex: 'outTimeStr',
      align: 'center',
    },
    {
      title: '格位',
      dataIndex: 'spaceNo',
      align: 'center',
    },
    {
      title: '車牌',
      dataIndex: 'lprNo',
      align: 'center',
    },
    {
      title: '查看照片',
      key: 'view',
      align: 'center',
      width: 80,
      render: (_, r) => (
        <IconPhoto
          onClick={() => {
            setPhotoModalData(r)
            refetch({ mainOrderNo: r?.mainOrderNo })
            photoModal.open()
          }}
        />
      ),
    },
  ]

  const [dates, setDates] = React.useState<RangeValue>(null)
  const [value, setValue] = React.useState<RangeValue>(null)

  const disabledDate = (current: Moment) => {
    if (!dates) {
      return false
    }
    const tooLate = dates[0] && current.diff(dates[0], 'days') > 14
    const tooEarly = dates[1] && dates[1].diff(current, 'days') > 14
    return !!tooEarly || !!tooLate
  }

  const onOpenChange = (open: boolean) => {
    if (open) {
      setDates([null, null])
    } else {
      setDates(null)
    }
  }

  const locationId = Form.useWatch('locationId', form) || null
  const reportDate = Form.useWatch('reportDate', form) || null
  const sessionId = Form.useWatch('sessionId', form) || null

  React.useEffect(() => {
    const _imageData: any = imageData
    setPhotoModalData({ ...photoModalData, ..._imageData?.data })
  }, [imageData])

  return (
    <>
      {photoModal.visible && (
        <ImageViewerModal
          item={[
            {
              url: `data:image/png;base64,${photoModalData?.image}`,
            },
          ]}
          isEdit={false}
          onCancel={photoModal.close}
          title=" "
          description={
            <span
              style={{
                color: '#FFF1B8',
              }}>
              停車起迄：{photoModalData?.inTimeStr}-{photoModalData?.outTimeStr}
              / 車格：{photoModalData?.spaceNo} / 車牌：
              {photoModalData?.lprNo}
            </span>
          }
        />
      )}
      <Layout>
        <Form layout="inline" form={form}>
          <FormItem
            name="reportDate"
            rules={[rules.required]}
            label={'停車起始時間'}
            className="w-264">
            <RangePicker
              value={dates || value}
              disabledDate={disabledDate}
              onCalendarChange={(val: any) => setDates(val)}
              onChange={(val: any) => setValue(val)}
              onOpenChange={onOpenChange}
              placeholder={['(必填)至多二週內之區間']}
            />
          </FormItem>

          {/* <FormItem label={'縣市'} name="cityId" className="w-130">
            <OptionsSelect.SessionSelect />
          </FormItem>

          <FormItem label={'行政區'} name="locationId" className="w-130">
            <OptionsSelect.SessionSelect />
          </FormItem> */}

          <FormItem
            label={'縣市/行政區'}
            name="locationId"
            className="w-[280px]">
            <OptionsSelect.CityAreaSelect
              className="w-130"
              supportAll={false}
              allowAllArea={false}
            />
          </FormItem>

          <FormItem
            label={'站點'}
            name="sessionId"
            rules={[rules.required]}
            className="w-130">
            <OptionsSelect.StandSelect
              placeholder="必填"
              queryId={locationId}
            />
          </FormItem>

          <FormItem
            name="input"
            label={t('device:search keyword')}
            className="w-264">
            <Input allowClear placeholder={'搜尋格位或車牌'} />
          </FormItem>

          <FormItem.Button>
            <Button
              loading={false}
              type="primary"
              className="w-96"
              onClick={handleSearch}>
              {t('common:search')}
            </Button>
          </FormItem.Button>
          <FormItem.Button>
            <Button
              loading={exporting}
              type="primary"
              ghost
              className="w-96"
              // onClick={handleExport}
            >
              {t('report:export')}
            </Button>
          </FormItem.Button>
        </Form>

        {isNil(locationId) || isNil(reportDate) || isNil(sessionId) ? (
          <div className="text-center mt-10">
            <p className="text-lg" style={{ color: '#8E8E8E' }}>
              請先輸入搜尋條件範圍
            </p>
          </div>
        ) : (
          <>
            <div className="mt-44 mb-16">
              <Flex between>
                <Table.RowCount count={dataSource.total} />
              </Flex>
            </div>
            <Table
              columns={columns}
              loading={loading}
              dataSource={dataSource}
              onChange={handleTableChange}
            />
          </>
        )}
      </Layout>
    </>
  )
}

export default React.memo(ParkingVideoRecord)
